import { Encoding, ViewFieldTypes } from '@mode-vegas/vegas';

import { lookupYField, RenderDataModel } from '@mode/charts/util-vegas';
import { InterActions } from '@mode/shared/contract-common';
import { ChartTypes, HighchartsEvents, FlamingoTypes } from '@mode/shared/contract-common';

export interface ChartEvent {
  click?: ChartEvent.ClickHandler;
  mouseOver?: ChartEvent.HoverHandler;
  mouseOut?: ChartEvent.HoverHandler;
}

export namespace ChartEvent {
  export type ClickHandler = (event: HighchartsEvents.ClickEvent) => boolean | void;
  export type HoverHandler = (event: HighchartsEvents.HoverEvent) => boolean | void;

  export function makeChartEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        handler({
          event,
          target: {
            id: target,
          },
          type: InterActions.Type.ChartClick,
        });
      },
    };
  }

  export function makeLineMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.LineChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
      mouseOut: (event: HighchartsEvents.HoverEvent) => {
        event.target.chart.tooltip.hide();
      },
    };
  }

  export function makeGroupedBarMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);
    const groupField = model.makeField(encoding.column[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                value: event.point.series.userOptions.name,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              columns: [
                {
                  name: groupField.name,
                  value: event.point.category,
                },
              ],

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.GroupedBarChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makeStackedBarMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.BarChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
      mouseOver: (event: HighchartsEvents.HoverEvent) => {
        event.target.update({
          zIndex: 1,
        });
      },
      mouseOut: (event: HighchartsEvents.HoverEvent) => {
        event.target.chart.tooltip.options.hideDelay = 1; // prevents flashing of tooltip when moving between segments in same bar
        event.target.chart.tooltip.hide();

        if (event.target.points.every((point) => point.state !== 'select')) {
          event.target.update({
            zIndex: 0,
          });
        }
      },
    };
  }

  export function makeComboMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    interface Series {
      readonly name: string;
      readonly yField: FlamingoTypes.ModeField;
      readonly groupField: FlamingoTypes.ModeField;
    }

    const seriesFields = new Array<Series>();
    const xField = model.makeField(encoding.x[0]);

    encoding.marks.series.forEach((series) => {
      const yEncField = lookupYField(encoding, series.field);
      const yField = model.makeField(yEncField);

      let colorField: FlamingoTypes.ModeField;
      if (series.properties.color && series.properties.color.values.length > 0) {
        colorField = model.makeField(series.properties.color.values[0]);
      } else {
        colorField = model.buildViewNamesField('Measure Names');
      }

      seriesFields.push({
        name: yField.name,
        groupField: colorField,
        yField,
      });
    });

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              // in combo, series index of 0 is y, 1 is y2
              ...(event.point.series.index === 0
                ? {
                    y: {
                      name: seriesFields[event.point.series.index].name,
                      value: event.point.y,
                    },
                  }
                : {
                    y2: {
                      name: seriesFields[event.point.series.index].name,
                      value: event.point.y,
                    },
                  }),

              series: {
                name: event.point.series.name,

                color: {
                  name: seriesFields[event.point.series.index].groupField.name,
                  color: event.point.color,
                  value: event.point.series.name,
                },
              },

              type: ChartTypes.TargetType.LinePlusBarChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
      mouseOut: (event: HighchartsEvents.HoverEvent) => {
        event.target.chart.tooltip.options.hideDelay = 1; // prevents flashing of tooltip when moving between segments in same bar
        event.target.chart.tooltip.hide();
      },
    };
  }

  export function makeAreaMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.AreaChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makeTotalAreaMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);

    let colorField: FlamingoTypes.ModeField;
    if (encoding.marks.series[0].properties.color.values.length) {
      colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);
    } else {
      colorField = model.buildViewNamesField('Measure Names');
    }

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.TotalAreaChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makeScatterMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);
    const colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.BubbleChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makeBubbleMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const xField = model.makeField(encoding.x[0]);
    const yField = model.makeField(encoding.y[0]);
    const sizeField = model.makeField(encoding.marks.series[0].properties.size.values[0]);
    const colorField = model.makeField(encoding.marks.series[0].properties.color.values[0]);

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              x: {
                name: xField.name,
                value: event.point.category,
              },

              y: {
                name: yField.name,
                value: event.point.y,
              },

              series: {
                // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                name: event.point.series.userOptions.name,

                size: {
                  name: sizeField.name,
                  size: event.point.marker.radius,
                  value: event.point.z,
                },

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  // Using userOptions.name in case the series name is null, in which case Highcharts overwrites with 'Series (index + 1)'
                  value: event.point.series.userOptions.name,
                },
              },

              type: ChartTypes.TargetType.BubbleChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makePieMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const colorField = model.makeField(encoding.marks.properties.color.values[0]);
    const angleField = model.makeField(encoding.marks.properties.angle.values[0]);

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              series: {
                name: event.point.series.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  value: event.point.options.name == null ? null : event.point.options.name, // if the value of the options.name doesn't exist that means Highcharts received a null value, therefore we need to reset it to null here
                },

                angle: {
                  name: angleField.name,
                  angle: event.point.percentage,
                  value: event.point.y,
                },
              },

              type: ChartTypes.TargetType.PieChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makeDonutMarkEvents(
    model: RenderDataModel,
    encoding: Encoding,
    target: string,
    handler?: InterActions.Handler
  ): ChartEvent {
    const colorField = model.makeField(encoding.marks.properties.color.values[0]);
    const angleField = model.makeField(encoding.marks.properties.angle.values[0]);

    return {
      click: (event: HighchartsEvents.ClickEvent) => {
        if (event.point && handler) {
          handler({
            event,
            target: {
              id: target,

              series: {
                name: event.point.series.name,

                color: {
                  name: colorField.name,
                  color: event.point.color,
                  value: event.point.options.name == null ? null : event.point.options.name, // if the value of the options.name doesn't exist that means Highcharts received a null value, therefore we need to reset it to null here
                },

                angle: {
                  name: angleField.name,
                  angle: event.point.percentage,
                  value: event.point.y,
                },
              },

              type: ChartTypes.TargetType.DonutChart,
            },

            position: {
              clientX: event.clientX,
              clientY: event.clientY,
            },

            type: InterActions.Type.MarkClick,
          });
        }
      },
    };
  }

  export function makeStackedBarSelectionEvents() {
    // the stacked bar selection state requires redrawing and deleting an independent rect element
    // in order to show a consistent border around the selected mark
    return {
      select: (event: HighchartsEvents.SelectEvent) => {
        const series = event.target.series;
        const shapeArgs = event.target.shapeArgs;
        const updatedX = shapeArgs.x + series.group.translateX;
        const updatedY = shapeArgs.y + series.group.translateY;
        const width = shapeArgs.width;
        const height = shapeArgs.height;

        series.chart.rect = series.chart.renderer
          .rect(updatedX, updatedY, width, height)
          .attr({
            'stroke-width': 1,
            zIndex: 999,
            class: 'updated-column-mark',
          })
          .add();
      },
      unselect: (event: HighchartsEvents.SelectEvent) => {
        if (event.target.selected) {
          event.target.series.chart.rect.destroy();
          delete event.target.series.chart.rect;
        }
      },
    };
  }
}
